/**
 * Where the console keeps its session token: in memory, mirrored to
 * sessionStorage so a reload keeps you signed in within the same tab.
 * Never localStorage: closing the tab ends the console session on this machine.
 */

const STORAGE_KEY = "walliyo-console-session";

export type SessionListener = (token: string | null) => void;

export interface SessionStore {
  get(): string | null;
  set(token: string): void;
  clear(): void;
  subscribe(listener: SessionListener): () => void;
}

/** The subset of the Storage API the session needs. Injected so tests don't need a browser. */
export type SessionBackend = Pick<Storage, "getItem" | "setItem" | "removeItem">;

function browserSessionStorage(): SessionBackend | null {
  try {
    return typeof window === "undefined" ? null : window.sessionStorage;
  } catch {
    // Blocked storage (privacy mode, disabled cookies): memory only.
    return null;
  }
}

export function createSessionStore(backend: SessionBackend | null = browserSessionStorage()): SessionStore {
  let token: string | null = null;
  try {
    token = backend?.getItem(STORAGE_KEY) ?? null;
  } catch {
    token = null;
  }
  const listeners = new Set<SessionListener>();
  const notify = () => listeners.forEach((listener) => listener(token));

  return {
    get: () => token,
    set(next) {
      token = next;
      try {
        backend?.setItem(STORAGE_KEY, next);
      } catch {
        // Memory still holds it; the session just won't survive a reload.
      }
      notify();
    },
    clear() {
      if (token === null) return;
      token = null;
      try {
        backend?.removeItem(STORAGE_KEY);
      } catch {
        // Nothing to do: memory is already cleared.
      }
      notify();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
